import React, { useMemo, useState, useEffect, useRef } from "react";
import {
  ArrowLeftOutlined,
  CalendarOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  ExclamationCircleFilled,
  EyeOutlined,
  MessageOutlined,
  PlusOutlined,
  RightOutlined,
  SearchOutlined,
  SendOutlined,
  TeamOutlined,
  UnorderedListOutlined,
  UserOutlined,
  WhatsAppOutlined,
  FileImageOutlined,
  FileOutlined,
  VideoCameraOutlined,
} from "@ant-design/icons";
import { Button, Input, Select, message, DatePicker, Spin, Tooltip } from "antd";
import { getAuthMethod, postAuthMethod, putAuthMethod } from "../../services/HttpServices";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import dayjs from "dayjs";
import WhatsAppCampaignHealthPanel from "./WhatsAppCampaignHealthPanel";
import WhatsAppTemplateComposer from "./template-composer/WhatsAppTemplateComposer";
import AssignmentStrategySelector from "./create-campaign/AssignmentStrategySelector";
import MediaPickerModal from "./MediaPickerModal";

const steps = [
  { key: 1, label: "Details" },
  { key: 2, label: "Audience" },
  { key: 3, label: "Message" },
  { key: 4, label: "Review" },
];

const categoryOptions = [
  {
    id: "marketing",
    title: "Marketing",
    description: "Promotional offers, announcements",
    chip: "Require Opt-in",
  },
  {
    id: "utility",
    title: "Utility",
    description: "Accept updates, confirmations, reminders",
    chip: "Transactional",
  },
  {
    id: "authentication",
    title: "Authentication",
    description: "One-time password, verification",
    chip: "OTP Only",
  },
];

const audienceTags = [
  "All",
  "VIP",
  "Repeat",
  "Payment Pending",
  "Inactive",
  "Responders",
];



const calendarDays = [
  ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
  ["1", "2", "3", "4", "5", "6", "7"],
  ["8", "9", "10", "11", "12", "13", "14"],
  ["15", "16", "17", "18", "19", "20", "21"],
  ["22", "23", "24", "25", "26", "27", "28"],
  ["29", "30", "31", "1", "2", "3", "4"],
];

const timeSlots = [
  ["09:00", "09:30", "10:00"],
  ["10:30", "11:00", "11:30"],
  ["10:30", "11:00", "11:30"],
  ["10:30", "11:00", "11:30"],
];

const CampaignStepper = ({ currentStep, onStepClick }) => (
  <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-center tw-gap-3 md:tw-gap-5">
    {steps.map((step, index) => {
      const done = step.key < currentStep;
      const active = step.key === currentStep;

      return (
        <React.Fragment key={step.key}>
          <div
            onClick={() => onStepClick?.(step.key)}
            className="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer hover:tw-opacity-80 tw-transition-opacity"
            role="button"
            tabIndex={0}
          >
            <div
              className={`tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-rounded-full tw-text-[11px] tw-font-semibold ${done
                  ? "tw-bg-[#16A34A] tw-text-white"
                  : active
                    ? "tw-bg-theme tw-text-white tw-shadow-[0_6px_18px_rgba(93,68,255,0.28)]"
                    : "tw-bg-secondary-color tw-text-sub-text"
                }`}
            >
              {done ? <CheckOutlined /> : step.key}
            </div>
            <span
              className={`tw-text-[12px] tw-font-medium ${done || active ? "tw-text-text-primary" : "tw-text-sub-text"
                }`}
            >
              {step.label}
            </span>
          </div>
          {index !== steps.length - 1 ? (
            <div
              className={`tw-h-px tw-w-[52px] md:tw-w-[78px] ${step.key < currentStep
                  ? "tw-bg-[#B9AEFF]"
                  : "tw-bg-border-secondary"
                }`}
            />
          ) : null}
        </React.Fragment>
      );
    })}
  </div>
);

const WhatsAppPhonePreview = ({ templateTitle, body, mappingConfig, selectedMediaObj }) => {
  // Format body to replace {{1}}, {{2}} etc with their mapped values or placeholders
  let formattedBody = body || "";
  if (formattedBody) {
    const matches = formattedBody.match(/\{\{(\d+)\}\}/g);
    if (matches) {
      matches.forEach(m => {
        const key = m.replace(/[{}]/g, '');
        let val = `{{${key}}}`;
        if (mappingConfig && mappingConfig.body && mappingConfig.body[key]) {
          const mapConfig = mappingConfig.body[key];
          if (mapConfig.type === 'constant' && mapConfig.value) {
            val = mapConfig.value;
          } else if (mapConfig.type === 'audience_field' && mapConfig.attribute) {
            val = `[${mapConfig.attribute}]`;
          }
        }
        formattedBody = formattedBody.replace(m, val);
      });
    }
  }

  // Prepend emoji if templateTitle exists and doesn't already have one
  const hasEmoji = /[\uD800-\uDFFF]/.test(templateTitle || "");
  const displayTitle = templateTitle
    ? hasEmoji
      ? templateTitle
      : `⏰ ${templateTitle}`
    : "⏰ Weekend Special!";

  return (
    <div className="tw-rounded-[24px] tw-border tw-border-solid tw-border-[#E2F0F9] tw-bg-[linear-gradient(180deg,#F0F9FF_0%,#ECFEFF_100%)] tw-p-5 tw-shadow-[0_12px_35px_rgba(8,145,178,0.05)]">
      <div className="tw-text-center tw-text-[11px] tw-font-bold tw-tracking-[0.5em] tw-text-[#7C8087] tw-uppercase tw-mb-3.5">
        PREVIEW
      </div>

      {/* Realistic Smartphone Mockup */}
      <div className="tw-mx-auto tw-relative tw-w-[270px] tw-h-[510px] tw-rounded-[38px] tw-border-[7px] tw-border-solid tw-border-[#111111] tw-bg-[#EFEAE2] tw-shadow-[0_15px_40px_rgba(0,0,0,0.15)] tw-flex tw-flex-col">
        {/* Dynamic Island / Notch */}
        <div className="tw-absolute tw-left-1/2 tw-top-1.5 tw-h-[16px] tw-w-[70px] -tw-translate-x-1/2 tw-rounded-full tw-bg-black tw-z-30 tw-after:content-[''] tw-after:absolute tw-after:right-3 tw-after:top-1.5 tw-after:h-1 tw-after:w-1 tw-after:rounded-full tw-after:bg-[#1a1a1a]" />

        {/* Screen Wrapper */}
        <div className="tw-relative tw-flex-1 tw-flex tw-flex-col tw-overflow-hidden tw-rounded-[30px] tw-bg-[#EFEAE2]">
          {/* Status Bar */}
          <div className="tw-flex tw-justify-between tw-items-center tw-px-6 tw-pt-3.5 tw-pb-1.5 tw-bg-[#008069] tw-text-white tw-text-[10px] tw-font-medium tw-z-20">
            <span>15:51 ..</span>
            <div className="tw-flex tw-items-center tw-gap-1.5">
              <span>📶</span>
              <div className="tw-flex tw-items-center tw-gap-0.5">
                <span>45%</span>
                <div className="tw-w-5 tw-h-2.5 tw-border tw-border-solid tw-border-white tw-rounded-[3px] tw-p-[1px] tw-flex tw-items-center tw-ml-0.5">
                  <div className="tw-w-[45%] tw-h-full tw-bg-white tw-rounded-[1px]" />
                </div>
              </div>
            </div>
          </div>

          {/* WhatsApp Header Bar */}
          <div className="tw-flex tw-items-center tw-justify-between tw-bg-[#008069] tw-px-4 tw-py-2 tw-text-white tw-z-20 tw-shadow-sm">
            <div className="tw-flex tw-items-center tw-gap-2">
              {/* Back Arrow */}
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                className="tw-cursor-pointer"
              >
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
              {/* Profile Avatar */}
              <div className="tw-w-8 tw-h-8 tw-rounded-full tw-bg-white/90 tw-flex tw-items-center tw-justify-center tw-text-gray-400 tw-overflow-hidden tw-border tw-border-solid tw-border-white/10 tw-shadow-sm">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                </svg>
              </div>
              {/* Profile Name */}
              <div className="tw-text-[12.5px] tw-font-bold tw-tracking-wide">
                John Smith
              </div>
            </div>
            {/* Action Icons */}
            <div className="tw-flex tw-items-center tw-gap-4">
              {/* Phone Icon */}
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="tw-cursor-pointer hover:tw-text-gray-200"
              >
                <path d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56a.977.977 0 0 0-1.01.24l-2.2 2.2a15.045 15.045 0 0 1-6.59-6.59l2.2-2.2c.28-.28.36-.67.25-1.02A11.36 11.36 0 0 1 8.5 4c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1 0 9.39 7.61 17 17 17 .55 0 1-.45 1-1v-3.5c0-.55-.45-1-1-1z" />
              </svg>
              {/* Video Icon */}
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="tw-cursor-pointer hover:tw-text-gray-200"
              >
                <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z" />
              </svg>
              {/* Three-dot menu */}
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="tw-cursor-pointer hover:tw-text-gray-200"
              >
                <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
              </svg>
            </div>
          </div>

          {/* Chat Messages Area */}
          <div className="tw-flex-1 tw-bg-[#E5DDD5] tw-p-4.5 tw-overflow-y-auto tw-flex tw-flex-col tw-justify-center">
            {/* WhatsApp Template Message Bubble */}
            <div className="tw-max-w-[90%] tw-mx-auto tw-bg-white tw-rounded-[12px] tw-shadow-[0_1px_2px_rgba(0,0,0,0.15)] tw-overflow-hidden tw-border tw-border-solid tw-border-gray-200/40">
              {/* Message Header */}
              <div className="tw-bg-[#F8F9FA] tw-px-4 tw-py-2.5 tw-border-b tw-border-l-0 tw-border-r-0 tw-border-t-0 tw-border-solid tw-border-gray-100 tw-flex tw-items-center tw-gap-2">
                <span className="tw-text-[12px] tw-font-bold tw-text-[#2E3133]">
                  {displayTitle}
                </span>
              </div>

              {/* Media Preview (if exists) */}
              {selectedMediaObj && (
                <div className="tw-p-1 tw-border-b tw-border-solid tw-border-gray-100 tw-bg-gray-50 tw-flex tw-items-center tw-justify-center">
                  {selectedMediaObj.mediaType === 'IMAGE' && selectedMediaObj.downloadUrl ? (
                    <img src={selectedMediaObj.downloadUrl} alt="media" className="tw-max-w-full tw-max-h-[160px] tw-rounded-md tw-object-contain" />
                  ) : (
                    <div className="tw-py-6 tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-gray-400">
                      {selectedMediaObj.mediaType === 'VIDEO' ? <VideoCameraOutlined className="tw-text-3xl" /> : (selectedMediaObj.mediaType === 'IMAGE' ? <FileImageOutlined className="tw-text-3xl" /> : <FileOutlined className="tw-text-3xl" />)}
                      <span className="tw-text-xs tw-mt-2 tw-text-center tw-truncate tw-max-w-[200px]">{selectedMediaObj.fileName}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Message Body */}
              <div className="tw-p-4 tw-text-[12px] tw-leading-[1.65] tw-text-[#2B2B2B] tw-whitespace-pre-line">
                {formattedBody}
                {/* Terms and Conditions apply */}
                <div className="tw-mt-3.5 tw-text-[10px] tw-text-[#8C9196] tw-font-normal">
                  Terms and Conditions apply
                </div>
              </div>

              {/* Action Buttons */}
              <div className="tw-flex tw-flex-col">
                <button
                  type="button"
                  className="tw-w-full tw-py-3 tw-text-center tw-text-[13px] tw-font-bold tw-text-blue-600 tw-bg-white tw-border-t tw-border-b-0 tw-border-r-0 tw-border-l-0 tw-border-solid tw-border-gray-100 hover:tw-bg-gray-50 tw-transition-colors tw-cursor-pointer tw-flex tw-items-center tw-justify-center tw-gap-1.5 tw-outline-none"
                >
                  <svg
                    width="13"
                    height="13"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    className="tw-mt-0.5"
                  >
                    <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"></path>
                  </svg>
                  Book Now
                </button>
                <button
                  type="button"
                  className="tw-w-full tw-py-3 tw-text-center tw-text-[13px] tw-font-bold tw-text-blue-600 tw-bg-white tw-border-t tw-border-b-0 tw-border-r-0 tw-border-l-0 tw-border-solid tw-border-gray-100 hover:tw-bg-gray-50 tw-transition-colors tw-cursor-pointer tw-outline-none"
                >
                  Not Interested
                </button>
              </div>
            </div>
          </div>

          {/* Bottom Chat Bar Mockup */}
          <div className="tw-bg-[#E5DDD5] tw-px-3 tw-py-2 tw-flex tw-items-center tw-gap-2 tw-z-20">
            <div className="tw-flex-1 tw-flex tw-items-center tw-bg-white tw-rounded-full tw-px-3 tw-py-1.5 tw-shadow-sm tw-gap-2">
              {/* Smiley Icon */}
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#8696A0"
                strokeWidth="2"
                className="tw-cursor-pointer"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M8 14s1.5 2 4 2 4-2 4-2M9 9h.01M15 9h.01" />
              </svg>
              <div className="tw-flex-1 tw-text-[13px] tw-text-gray-400 tw-px-1" />
              {/* Attachment Icon */}
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#8696A0"
                strokeWidth="2"
                className="tw-cursor-pointer"
              >
                <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
              </svg>
              {/* Camera Icon */}
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#8696A0"
                strokeWidth="2"
                className="tw-cursor-pointer"
              >
                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                <circle cx="12" cy="13" r="4" />
              </svg>
            </div>
            {/* Green Mic Button */}
            <div className="tw-w-9 tw-h-9 tw-rounded-full tw-bg-[#008069] tw-flex tw-items-center tw-justify-center tw-text-white tw-shadow-sm tw-flex-shrink-0 tw-cursor-pointer">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z" />
                <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                <line x1="12" y1="19" x2="12" y2="22" />
              </svg>
            </div>
          </div>

          {/* Android Navigation Bar */}
          <div className="tw-bg-white tw-py-2 tw-flex tw-justify-around tw-items-center tw-border-t tw-border-b-0 tw-border-r-0 tw-border-l-0 tw-border-solid tw-border-gray-100 tw-z-20">
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#6B7280"
              strokeWidth="2.5"
              className="tw-cursor-pointer"
            >
              <polygon points="18 4 6 12 18 20 18 4" />
            </svg>
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#6B7280"
              strokeWidth="2.5"
              className="tw-cursor-pointer"
            >
              <circle cx="12" cy="12" r="8" />
            </svg>
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#6B7280"
              strokeWidth="2.5"
              className="tw-cursor-pointer"
            >
              <rect x="5" y="5" width="14" height="14" rx="2" />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
};

const CampaignCategoryCard = ({ option, active, onSelect }) => (
  <button
    type="button"
    onClick={() => onSelect(option.id)}
    className={`tw-rounded-[12px] tw-border tw-border-solid tw-p-4 tw-text-left tw-transition-all ${active
        ? "tw-border-theme tw-bg-[#F7F4FF] tw-shadow-[0_10px_24px_rgba(93,68,255,0.08)]"
        : "tw-border-border-secondary tw-bg-[#FBFBFB]"
      }`}
  >
    <div className="tw-flex tw-items-start tw-gap-3">
      <div className="tw-flex !tw-h-7 !tw-w-7 tw-items-center tw-justify-center tw-rounded-full tw-bg-secondary-color tw-text-sub-text">
        <MessageOutlined className="tw-text-[12px]" />
      </div>
      <div>
        <div className="tw-text-sm tw-font-medium tw-text-text-primary">
          {option.title}
        </div>
        <div className="tw-mt-1 tw-text-[10px] tw-text-sub-text">
          {option.description}
        </div>
        <span className="tw-mt-3 tw-inline-flex tw-rounded-full tw-bg-[#F4F4F4] tw-px-2.5 tw-py-1 tw-text-[10px] tw-text-sub-text">
          {option.chip}
        </span>
      </div>
    </div>
  </button>
);

const AudienceCard = ({ list, active, onSelect }) => (
  <button
    type="button"
    onClick={() => onSelect(list.id)}
    className={`tw-relative tw-rounded-2xl tw-border tw-border-solid tw-p-4 tw-text-left tw-transition-all tw-duration-200 tw-outline-none tw-w-full ${
      active
        ? "tw-bg-[linear-gradient(135deg,#F5F3FF_0%,#FFFFFF_100%)] tw-border-[#5D44FF] tw-shadow-[0_8px_24px_rgba(93,68,255,0.14)] tw-ring-1 tw-ring-[#5D44FF]"
        : "tw-bg-white tw-border-slate-200/90 hover:tw-border-slate-300 hover:tw-shadow-md hover:-tw-translate-y-0.5"
    }`}
  >
    <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
      <div className={`tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-xl tw-transition-colors ${
        active ? "tw-bg-[#5D44FF] tw-text-white" : "tw-bg-[#5D44FF]/10 tw-text-[#5D44FF]"
      }`}>
        <TeamOutlined className="tw-text-sm" />
      </div>
      {active && (
        <span className="tw-w-5 tw-h-5 tw-rounded-full tw-bg-[#5D44FF] tw-text-white tw-flex tw-items-center tw-justify-center tw-text-[11px] tw-font-bold tw-shadow-xs">
          ✓
        </span>
      )}
    </div>

    <div className="tw-text-base tw-font-bold tw-text-slate-800 tw-truncate tw-mb-0.5" title={list.name}>
      {list.name}
    </div>
    <div className="tw-text-[11px] tw-font-medium tw-text-slate-400 tw-mb-3">
      {list.lastUsed}
    </div>

    <div className="tw-flex tw-items-baseline tw-gap-1.5">
      <span className="tw-text-2xl tw-font-extrabold tw-text-slate-900 tw-leading-none">
        {list.contacts}
      </span>
      <span className="tw-text-xs tw-font-medium tw-text-slate-500">
        WhatsApp Ready Contacts
      </span>
    </div>
  </button>
);

const TemplateCard = ({ template, active, onSelect }) => {
  const title = template.name || template.title || "Template";
  let body = template.body || "";
  if (!body && template.components) {
    let comps = template.components;
    if (typeof comps === 'string') {
      try { comps = JSON.parse(comps); } catch (e) { comps = []; }
    }
    if (Array.isArray(comps)) {
      const bodyComp = comps.find((c) => c.type === "BODY");
      if (bodyComp) body = bodyComp.text;
    }
  }

  return (
    <div
      onClick={() => onSelect(template.id)}
      className={`tw-relative tw-rounded-2xl tw-border tw-border-solid tw-p-4 tw-text-left tw-cursor-pointer tw-transition-all tw-duration-200 tw-flex tw-flex-col tw-justify-between tw-h-full ${
        active
          ? "tw-bg-[linear-gradient(135deg,#F5F3FF_0%,#FFFFFF_100%)] tw-border-[#5D44FF] tw-shadow-[0_8px_24px_rgba(93,68,255,0.14)] tw-ring-1 tw-ring-[#5D44FF]"
          : "tw-bg-white tw-border-slate-200/90 hover:tw-border-slate-300 hover:tw-shadow-md hover:-tw-translate-y-0.5"
      }`}
    >
      <div>
        <div className="tw-flex tw-items-start tw-justify-between tw-gap-2 tw-mb-2">
          <div className="tw-text-base tw-font-bold tw-text-slate-800 tw-truncate" title={title}>
            {title}
          </div>
          {active && (
            <span className="tw-w-5 tw-h-5 tw-rounded-full tw-bg-[#5D44FF] tw-text-white tw-flex tw-items-center tw-justify-center tw-text-[11px] tw-font-bold tw-flex-shrink-0 tw-shadow-xs">
              ✓
            </span>
          )}
        </div>

        <div className="tw-text-xs tw-text-slate-600 tw-line-clamp-2 tw-leading-relaxed tw-mb-3 tw-min-h-[36px]">
          {body || "No body preview available"}
        </div>
      </div>

      <div className="tw-flex tw-items-center tw-justify-between tw-pt-2.5 tw-mt-2 tw-border-t tw-border-b-0 tw-border-l-0 tw-border-r-0 tw-border-solid tw-border-slate-100">
        <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-emerald-50 tw-px-2.5 tw-py-1 tw-text-[10px] tw-font-bold tw-text-emerald-700 tw-border tw-border-solid tw-border-emerald-200/60">
          <span className="tw-h-1.5 tw-w-1.5 tw-rounded-full tw-bg-emerald-500" />
          {template.status || "APPROVED"}
        </span>

        {template.category && (
          <span className="tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-wider tw-text-indigo-600 tw-bg-indigo-50 tw-px-2.5 tw-py-1 tw-rounded-md">
            {template.category}
          </span>
        )}
      </div>
    </div>
  );
};

const DetailsStep = ({
  wabaInfo,
  whatsappPhoneNumberId,
  campaignName,
  language,
  isPublic,
  errors = {},
  onIsPublicChange,
  onPhoneNumberChange,
  onCampaignNameChange,
  assignmentStrategy,
  onAssignmentStrategyChange,
  assignmentWeights,
  onAssignmentWeightsChange,
}) => (
  <div className="tw-max-w-4xl tw-mx-auto tw-space-y-4">
    <div className="tw-rounded-2xl tw-border tw-border-solid tw-border-slate-200/80 tw-bg-white tw-p-5 tw-shadow-sm md:tw-p-6">
      <div className="tw-space-y-7">
        <div>
          <div className="tw-mb-2 tw-text-xs tw-font-bold tw-text-slate-700 tw-uppercase tw-tracking-wider tw-flex tw-items-center tw-gap-1">
            Campaign Name <span className="tw-text-rose-500">*</span>
          </div>
          <Input
            value={campaignName}
            onChange={(event) => onCampaignNameChange(event.target.value)}
            placeholder="e.g. Summer Campaign"
            status={errors?.campaignName ? "error" : ""}
            className={`!tw-h-[42px] !tw-bg-white !tw-rounded-xl !tw-text-slate-800 !tw-text-xs !tw-font-medium placeholder:!tw-text-slate-400 ${errors?.campaignName
                ? "!tw-border-rose-500 focus:!tw-border-rose-500"
                : "!tw-border-slate-200/80 focus:!tw-border-[#5D44FF]"
              }`}
          />
          {errors?.campaignName && (
            <div className="tw-mt-1.5 tw-text-xs tw-text-rose-500 tw-font-medium">
              {errors.campaignName}
            </div>
          )}
        </div>

        <div>
          <div className="tw-mb-2 tw-text-sm tw-font-medium tw-text-text-primary">
            Visibility
          </div>
          <div className="tw-flex tw-items-center tw-gap-3">
            <span className="tw-text-sm tw-text-sub-text">Private</span>
            <div
              onClick={() => onIsPublicChange(!isPublic)}
              className={`tw-w-11 tw-h-6 tw-rounded-full tw-relative tw-cursor-pointer tw-transition-colors ${isPublic ? 'tw-bg-[#34C759]' : 'tw-bg-gray-300'}`}
            >
              <div className={`tw-w-5 tw-h-5 tw-rounded-full tw-bg-white tw-absolute tw-top-0.5 tw-transition-all ${isPublic ? 'tw-left-[22px]' : 'tw-left-0.5'}`} />
            </div>
            <span className="tw-text-sm tw-text-text-primary tw-font-medium">Public</span>
          </div>
          <div className="tw-text-xs tw-text-sub-text tw-mt-1">
            {isPublic ? "Visible to all team members" : "Only visible to you"}
          </div>
        </div>

        <div>
          <div className="tw-mb-2 tw-text-sm tw-font-medium tw-text-text-primary tw-flex tw-items-center tw-gap-1">
            Sender Phone Number <span className="tw-text-rose-500">*</span>
          </div>
          <Select
            value={whatsappPhoneNumberId}
            onChange={onPhoneNumberChange}
            placeholder="Select a phone number"
            status={errors?.whatsappPhoneNumberId ? "error" : ""}
            className="tw-h-[42px] tw-w-full"
            options={wabaInfo?.phoneNumbers?.map(pn => ({
              label: `${pn.displayPhoneNumber} (Active)`,
              value: pn.id
            })) || []}
          />
          {errors?.whatsappPhoneNumberId && (
            <div className="tw-mt-1.5 tw-text-xs tw-text-rose-500 tw-font-medium">
              {errors.whatsappPhoneNumberId}
            </div>
          )}
          {wabaInfo && wabaInfo.businessName && (
            <div className="tw-mt-1 tw-text-xs tw-text-gray-400">
              Sending from {wabaInfo.businessName}
            </div>
          )}
        </div>
      </div>
    </div>

    {/* Assignment Strategy Selector */}
    <AssignmentStrategySelector
      assignmentStrategy={assignmentStrategy}
      onStrategyChange={onAssignmentStrategyChange}
      assignmentWeights={assignmentWeights}
      onWeightsChange={onAssignmentWeightsChange}
    />
  </div>
);

const AudienceStep = ({ contactLists, selectedListIds, errors = {}, onSelectList, onContinue, onSaveDraft, selectedTag, searchQuery, onTagSelect, onSearchChange, phoneNumbers, audienceTags = ["All"] }) => {
  const navigate = useNavigate();
  const selectedAudiences = contactLists.filter((item) => selectedListIds.includes(item.id));
  const totalContacts = selectedAudiences.reduce((acc, curr) => acc + curr.contacts, 0);

  const filteredContactLists = useMemo(() => {
    return (contactLists || []).filter((list) => {
      const matchesSearch = !searchQuery?.trim() || 
        list.name?.toLowerCase().includes(searchQuery.trim().toLowerCase());
      
      const matchesTag = !selectedTag || selectedTag === "All" || 
        (list.tags && list.tags.includes(selectedTag));

      return matchesSearch && matchesTag;
    });
  }, [contactLists, searchQuery, selectedTag]);

  return (
    <div className="tw-mx-auto tw-max-w-[980px]">

      {/* Health panel moved to global sticky right sidebar */}

      <div className="tw-flex tw-flex-col tw-gap-4 md:tw-flex-row md:tw-items-center md:tw-justify-end">
        <div className="tw-flex tw-flex-wrap tw-gap-2.5">
          <Button
            onClick={() => navigate("/campaigns/audience")}
            className="!tw-h-[42px] !tw-bg-white hover:!tw-bg-slate-50 !tw-border-slate-200/80 !tw-rounded-xl !tw-px-4 tw-text-xs tw-font-semibold tw-text-slate-700 tw-shadow-sm"
          >
            <UnorderedListOutlined />
            View all lists
          </Button>
          <Button
            type="primary"
            onClick={() => navigate("/campaigns/audience/create")}
            className="!tw-bg-[#5D44FF] !tw-border-[#5D44FF] !tw-h-[42px] !tw-rounded-xl !tw-px-4 tw-text-xs tw-font-semibold tw-flex tw-items-center tw-gap-2 hover:!tw-bg-[#5D44FF]/90 tw-shadow-sm tw-shadow-[#5D44FF]/25"
          >
            <PlusOutlined />
            Create List
          </Button>
        </div>
      </div>

      <div className="tw-mt-5 tw-bg-slate-50/50 tw-p-3.5 tw-rounded-2xl tw-border tw-border-solid tw-border-slate-100">
        <div className="tw-flex tw-items-center tw-gap-2 tw-overflow-x-auto tw-flex-nowrap tw-py-1 tw-w-full [&::-webkit-scrollbar]:tw-h-1.5 [&::-webkit-scrollbar-track]:tw-bg-transparent [&::-webkit-scrollbar-thumb]:tw-bg-slate-200 [&::-webkit-scrollbar-thumb]:tw-rounded-full hover:[&::-webkit-scrollbar-thumb]:tw-bg-slate-300">
          {audienceTags.map((tag) => {
            const active = selectedTag === tag;
            return (
              <button
                type="button"
                key={tag}
                onClick={() => onTagSelect(tag)}
                className={`tw-inline-flex tw-items-center tw-gap-2 tw-rounded-full tw-px-3.5 tw-py-1.5 tw-text-xs tw-font-bold tw-border tw-border-solid tw-cursor-pointer tw-transition-all tw-whitespace-nowrap tw-shadow-xs tw-flex-shrink-0 ${active
                    ? "tw-bg-[#5D44FF] tw-text-white tw-border-[#5D44FF] tw-shadow-sm tw-shadow-[#5D44FF]/25"
                    : "tw-bg-white tw-text-slate-800 tw-border-slate-200/90 hover:tw-border-slate-300 hover:tw-bg-slate-50"
                  }`}
              >
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className={`tw-flex-shrink-0 ${active ? "tw-text-white" : "tw-text-slate-400"}`}
                >
                  <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
                  <line x1="7" y1="7" x2="7.01" y2="7"></line>
                </svg>
                <span>{tag}</span>
              </button>
            );
          })}
        </div>
      </div>


      <div className="tw-mt-6">
        <label className="tw-block tw-text-sm tw-font-bold tw-text-slate-800 tw-mb-2.5 tw-flex tw-items-center tw-gap-1">
          Select Contact Lists <span className="tw-text-rose-500">*</span>
        </label>
        <Input
          prefix={<SearchOutlined className="tw-text-slate-400 tw-text-base tw-mr-1" />}
          placeholder="Search contact lists..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          allowClear
          status={errors?.audience ? "error" : ""}
          className="!tw-h-11 !tw-w-full !tw-rounded-xl !tw-border-slate-200/90 !tw-text-sm !tw-font-medium placeholder:!tw-text-slate-400 focus:!tw-border-[#5D44FF]"
        />
        {errors?.audience && (
          <div className="tw-mt-1.5 tw-text-xs tw-text-rose-500 tw-font-medium">
            {errors.audience}
          </div>
        )}
      </div>

      {filteredContactLists.length === 0 ? (
        <div className="tw-mt-4 tw-rounded-2xl tw-border tw-border-solid tw-border-slate-200/90 tw-bg-white tw-px-6 tw-py-6 tw-text-center tw-shadow-sm tw-flex tw-flex-col tw-items-center tw-justify-center tw-min-h-[160px]">
          <div className="tw-mx-auto tw-mb-2.5 tw-flex tw-h-11 tw-w-11 tw-items-center tw-justify-center tw-rounded-xl tw-bg-indigo-50 tw-text-[#5D44FF] tw-border tw-border-solid tw-border-indigo-100/80">
            <SearchOutlined className="tw-text-lg" />
          </div>
          <h3 className="tw-mb-1 tw-text-sm tw-font-bold tw-text-slate-800">
            {searchQuery ? `No audiences matching "${searchQuery}"` : "No audience lists found"}
          </h3>
          <p className="tw-mx-auto tw-max-w-md tw-text-[11.5px] tw-text-slate-500 tw-leading-normal tw-mb-0">
            {searchQuery 
              ? "We couldn't find any contact lists matching your search query. Try checking for typos or clear your search."
              : "No contact lists are available for the selected tag filter."}
          </p>
        </div>
      ) : (
        <div className="tw-mt-4 tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-2 xl:tw-grid-cols-4">
          {filteredContactLists.slice(0, 4).map((list, index) => (
            <AudienceCard
              key={`${list.id}-${index}`}
              list={list}
              active={selectedListIds.includes(list.id)}
              onSelect={(id) => {
                if (selectedListIds.includes(id)) {
                  onSelectList(selectedListIds.filter(v => v !== id));
                } else {
                  onSelectList([...selectedListIds, id]);
                }
              }}
            />
          ))}
        </div>
      )}

      <div className="tw-mt-4 tw-flex tw-flex-col tw-gap-4 tw-rounded-2xl tw-border tw-border-solid tw-border-slate-200/80 tw-bg-white tw-px-5 tw-py-3.5 tw-shadow-sm md:tw-flex-row md:tw-items-center md:tw-justify-between">
        <div className="tw-flex tw-items-center tw-gap-3">
          <div className="tw-flex tw-h-9 tw-w-9 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-xl tw-bg-[#5D44FF]/10 tw-text-[#5D44FF]">
            <TeamOutlined className="tw-text-sm" />
          </div>
          <div>
            <div className="tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-wider tw-text-slate-400">
              SELECTED AUDIENCES ({selectedAudiences.length})
            </div>
            <div className="tw-mt-0.5 tw-text-sm tw-font-bold tw-text-slate-800">
              {selectedAudiences.length > 0 ? selectedAudiences.map(a => a.name).join(", ") : "None selected"}
              <span className="tw-font-semibold tw-text-slate-700">
                {" "}
                • {totalContacts} Contacts
              </span>
            </div>
          </div>
        </div>

        <div className="tw-flex tw-items-center tw-gap-0">
          <div className="tw-px-6 tw-text-right">
            <div className="tw-text-[9px] tw-font-semibold tw-tracking-[0.16em] tw-text-gray-400">
              EST. REACH
            </div>
            <div className="tw-mt-0.5 tw-text-[22px] tw-font-bold tw-text-text-primary tw-leading-none">
              {totalContacts}
            </div>
          </div>
          {/* Vertical divider */}
          <div className="tw-h-10 tw-w-px tw-bg-gray-200 tw-flex-shrink-0" />
          <div className="tw-pl-4 tw-flex tw-items-center tw-gap-3">
            {onSaveDraft && (
              <button
                type="button"
                onClick={onSaveDraft}
                className="tw-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-white hover:tw-bg-slate-50 tw-border tw-border-solid tw-border-slate-200/90 tw-px-5 tw-py-2.5 tw-text-[13px] tw-font-semibold tw-text-slate-700 tw-cursor-pointer tw-transition-colors tw-shadow-sm"
              >
                <FileOutlined />
                Save Draft
              </button>
            )}
            <button
              type="button"
              onClick={onContinue}
              className="tw-flex tw-items-center tw-gap-2 tw-rounded-full tw-bg-[#5D44FF] hover:tw-bg-[#4B34E6] tw-px-6 tw-py-2.5 tw-text-[13px] tw-font-semibold tw-text-white tw-border-none tw-cursor-pointer tw-transition-colors tw-outline-none"
            >
              Continue
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const MessageStep = ({ templates, selectedTemplateId, errors = {}, onSelectTemplate, onContinue, onSaveDraft, mappingConfig, onMappingChange, fetchTemplates, selectedMediaObj, onMediaSelect }) => {
  const [isCreateTemplateOpen, setIsCreateTemplateOpen] = useState(false);
  const [isMediaPickerVisible, setIsMediaPickerVisible] = useState(false);
  const [templateSearchQuery, setTemplateSearchQuery] = useState("");
  const campaignFileInputRef = useRef(null);

  const approvedTemplates = useMemo(() => {
    return (templates || []).filter(
      (t) => !t.status || t.status === "APPROVED"
    );
  }, [templates]);

  const sortedTemplates = useMemo(() => {
    return [...approvedTemplates].sort((a, b) => {
      const timeA = a.createdDate ? new Date(a.createdDate).getTime() : (typeof a.id === 'number' ? a.id : 0);
      const timeB = b.createdDate ? new Date(b.createdDate).getTime() : (typeof b.id === 'number' ? b.id : 0);
      return timeB - timeA;
    });
  }, [approvedTemplates]);

  const displayedTemplates = useMemo(() => {
    if (!templateSearchQuery.trim()) {
      return sortedTemplates.slice(0, 4);
    }
    const q = templateSearchQuery.toLowerCase();
    return sortedTemplates.filter((t) => {
      const title = (t.name || t.title || "").toLowerCase();
      const cat = (t.category || "").toLowerCase();
      let body = (t.body || "").toLowerCase();
      if (t.components && typeof t.components === "object") {
        try {
          const comps = typeof t.components === "string" ? JSON.parse(t.components) : t.components;
          const bComp = comps?.find?.((c) => c.type === "BODY");
          if (bComp?.text) body = bComp.text.toLowerCase();
        } catch (e) {}
      }
      return title.includes(q) || cat.includes(q) || body.includes(q);
    });
  }, [sortedTemplates, templateSearchQuery]);

  const selectedTemplate =
    approvedTemplates.find((item) => item.id === selectedTemplateId) || approvedTemplates[0] || {};

  const getVariablesFromTemplate = (t) => {
    let vars = [];
    if (!t) return vars;
    let comps = t.components;
    if (typeof comps === 'string') {
      try { comps = JSON.parse(comps); } catch (e) { comps = []; }
    }
    if (Array.isArray(comps)) {
      const bodyComp = comps.find(c => c.type === 'BODY');
      if (bodyComp && bodyComp.text) {
        const matches = bodyComp.text.match(/\{\{(\d+)\}\}/g);
        if (matches) {
          vars = [...new Set(matches.map(m => m.replace(/[{}]/g, '')))];
        }
      }
    }
    return vars;
  };

  const getHeaderComponent = (t) => {
    if (!t) return null;
    let comps = t.components;
    if (typeof comps === 'string') {
      try { comps = JSON.parse(comps); } catch (e) { comps = []; }
    }
    if (Array.isArray(comps)) {
      return comps.find(c => c.type === 'HEADER');
    }
    return null;
  };

  const variables = getVariablesFromTemplate(selectedTemplate);
  const headerComponent = getHeaderComponent(selectedTemplate);
  const requiresMedia = headerComponent && ['IMAGE', 'VIDEO', 'DOCUMENT'].includes((headerComponent.format || "").toUpperCase());

  const handleMappingChange = (varKey, value, type) => {
    const newMapping = { ...mappingConfig };
    if (!newMapping.body) newMapping.body = {};
    newMapping.body[varKey] = { type, [type === 'audience_field' ? 'attribute' : 'value']: value };
    if (type === 'constant' && !value) {
      newMapping.body[varKey] = { type: 'constant', value: '' };
    }
    onMappingChange(newMapping);
  };

  const isMappingComplete = (variables.length === 0 || variables.every(v => {
    const map = mappingConfig?.body?.[v];
    if (!map) return false;
    if (map.type === 'audience_field' && map.attribute) return true;
    if (map.type === 'constant' && map.value) return true;
    return false;
  })) && (!requiresMedia || selectedMediaObj);

  const getBodyPreview = (t) => {
    let text = t.body || "";
    if (t.components) {
      let comps = t.components;
      if (typeof comps === 'string') {
        try { comps = JSON.parse(comps); } catch (e) { comps = []; }
      }
      if (Array.isArray(comps)) {
        const bodyComp = comps.find(c => c.type === 'BODY');
        if (bodyComp) text = bodyComp.text;
      }
    }
    return text;
  };

  const bodyText = getBodyPreview(selectedTemplate);
  const title = selectedTemplate.name || selectedTemplate.title || "Template";

  const getAcceptString = (fmt) => {
    switch ((fmt || "").toUpperCase()) {
      case "IMAGE": return "image/*";
      case "VIDEO": return "video/*";
      case "DOCUMENT": return ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt";
      default: return "*/*";
    }
  };

  const handleDirectMediaSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const hide = message.loading("Uploading media file...", 0);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("mediaType", headerComponent?.format?.toUpperCase() || "IMAGE");
      formData.append("title", file.name);

      const res = await postAuthMethod("/api/media", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      hide();
      if (res?.data) {
        message.success("Media uploaded successfully");
        onMediaSelect(res.data);
      } else {
        message.error("Failed to upload media");
      }
    } catch (err) {
      hide();
      console.error("Failed to upload media", err);
      message.error("Failed to upload media");
    } finally {
      if (e.target) e.target.value = "";
    }
  };

  return (
    <div className="tw-grid tw-grid-cols-1 tw-gap-6 xl:tw-grid-cols-[1.45fr_0.8fr]">
      <div>
        <div className="tw-mb-3 tw-flex tw-flex-col tw-gap-3 md:tw-flex-row md:tw-items-center md:tw-justify-between">
          <div className="tw-text-[14px] tw-font-semibold tw-text-text-primary tw-flex tw-items-center tw-gap-1">
            Choose a pre-approved template <span className="tw-text-rose-500">*</span>
          </div>
          <Button
            type="primary"
            onClick={() => setIsCreateTemplateOpen(true)}
            className="!tw-h-10 !tw-rounded-full !tw-border-0 !tw-bg-theme !tw-px-4 tw-cursor-pointer"
          >
            <PlusOutlined />
            Create From Scratch
          </Button>
        </div>
        {errors?.template && (
          <div className="tw-mb-3 tw-text-xs tw-text-rose-500 tw-font-medium">
            {errors.template}
          </div>
        )}
        <div className="tw-mb-3">
          <Input
            prefix={<SearchOutlined className="tw-text-sub-text" />}
            placeholder="Search templates..."
            value={templateSearchQuery}
            onChange={(e) => setTemplateSearchQuery(e.target.value)}
            allowClear
            className="tw-h-11 tw-w-full tw-rounded-[14px]"
          />
        </div>

        {approvedTemplates.length === 0 ? (
          <div className="tw-rounded-2xl tw-border tw-border-solid tw-border-slate-200/90 tw-bg-white tw-p-8 tw-text-center tw-shadow-sm tw-my-4">
            <div className="tw-w-14 tw-h-14 tw-rounded-2xl tw-bg-[#25D366]/10 tw-text-[#25D366] tw-flex tw-items-center tw-justify-center tw-mx-auto tw-mb-4 tw-border tw-border-solid tw-border-[#25D366]/20">
              <MessageOutlined className="tw-text-2xl" />
            </div>
            <h3 className="tw-text-lg tw-font-bold tw-text-slate-800 tw-mb-2">
              No Approved WhatsApp Templates Available
            </h3>
            <p className="tw-text-xs sm:tw-text-sm tw-text-slate-600 tw-max-w-md tw-mx-auto tw-mb-6 tw-leading-relaxed">
              You don't have any WhatsApp message templates created or approved yet. Create a new message template to send with this campaign.
            </p>
            <Button
              type="primary"
              onClick={() => setIsCreateTemplateOpen(true)}
              className="!tw-h-11 !tw-px-6 !tw-bg-[#25D366] hover:!tw-bg-[#20ba56] !tw-border-none !tw-text-white !tw-rounded-xl !tw-font-bold tw-inline-flex tw-items-center tw-gap-2 !tw-shadow-[0_4px_12px_rgba(37,211,102,0.25)]"
            >
              <PlusOutlined />
              Create WhatsApp Template
            </Button>
          </div>
        ) : (
          <>
            {displayedTemplates.length === 0 ? (
              <div className="tw-my-4 tw-rounded-2xl tw-border tw-border-solid tw-border-slate-200/90 tw-bg-white tw-px-6 tw-py-8 tw-text-center tw-shadow-sm">
                <div className="tw-mx-auto tw-mb-3 tw-flex tw-h-12 tw-w-12 tw-items-center tw-justify-center tw-rounded-2xl tw-bg-indigo-50 tw-text-[#5D44FF] tw-border tw-border-solid tw-border-indigo-100/80">
                  <SearchOutlined className="tw-text-lg" />
                </div>
                <h3 className="tw-mb-1 tw-text-sm tw-font-bold tw-text-slate-800">
                  No templates matching "{templateSearchQuery}"
                </h3>
                <p className="tw-mx-auto tw-max-w-md tw-text-[11.5px] tw-text-slate-500 tw-leading-relaxed tw-mb-0">
                  We couldn't find any approved templates matching your search query. Try checking for typos or clear your search to view all pre-approved templates.
                </p>
              </div>
            ) : (
              <div className="tw-grid tw-grid-cols-1 tw-gap-3.5 md:tw-grid-cols-2">
                {displayedTemplates.map((template) => (
                  <TemplateCard
                    key={template.id}
                    template={template}
                    active={selectedTemplateId === template.id}
                    onSelect={onSelectTemplate}
                  />
                ))}
              </div>
            )}
            
            {(variables.length > 0 || requiresMedia) && (
              <div className="tw-mt-5 tw-rounded-2xl tw-border tw-border-solid tw-border-slate-200/90 tw-bg-white tw-p-5 tw-shadow-sm">
                <div className="tw-text-base tw-font-bold tw-text-slate-800 tw-flex tw-items-center tw-gap-2 tw-mb-4 tw-pb-3 tw-border-b tw-border-t-0 tw-border-r-0 tw-border-l-0 tw-border-solid tw-border-slate-100">
                  <div className="tw-flex tw-h-7 tw-w-7 tw-items-center tw-justify-center tw-rounded-xl tw-bg-[#5D44FF]/10 tw-text-[#5D44FF] tw-text-xs tw-font-bold">
                    ⚡
                  </div>
                  Template Configurations
                </div>

                <div className="tw-space-y-5">
                  {variables.length > 0 && (
                    <div>
                      <label className="tw-text-xs tw-font-bold tw-text-slate-700 tw-uppercase tw-tracking-wider tw-block tw-mb-2.5">
                        Map Template Variables <span className="tw-text-rose-500">*</span>
                      </label>
                      {errors?.templateMapping && (
                        <div className="tw-mb-2.5 tw-text-xs tw-text-rose-500 tw-font-medium">
                          {errors.templateMapping}
                        </div>
                      )}
                      <div className="tw-space-y-2.5">
                        {variables.map((v) => {
                          const currentMap = mappingConfig?.body?.[v];
                          const selectValue = currentMap?.type === 'constant' ? 'custom' : currentMap?.attribute || null;

                          return (
                            <div key={v} className="tw-flex tw-items-center tw-gap-3">
                              <span className="tw-font-mono tw-text-xs tw-font-bold tw-text-[#5D44FF] tw-bg-[#5D44FF]/10 tw-px-3 tw-py-2 tw-rounded-xl tw-shrink-0">
                                {`{{${v}}}`}
                              </span>
                              <div className="tw-flex-1 tw-min-w-0">
                                <Select
                                  className="tw-w-full"
                                  placeholder="Select contact field..."
                                  value={selectValue}
                                  onChange={(val) => {
                                    if (val === 'custom') {
                                      handleMappingChange(v, '', 'constant');
                                    } else {
                                      handleMappingChange(v, val, 'audience_field');
                                    }
                                  }}
                                  style={{ height: "40px" }}
                                  options={[
                                    { label: "Contact First Name", value: "firstName" },
                                    { label: "Contact Last Name", value: "lastName" },
                                    { label: "Phone Number", value: "phoneNumber" },
                                    { label: "Email Address", value: "email" },
                                    { label: "Custom Static Text...", value: "custom" },
                                  ]}
                                />
                              </div>
                              {currentMap?.type === 'constant' && (
                                <Input
                                  placeholder="Enter custom value..."
                                  value={currentMap?.value || ''}
                                  onChange={(e) => handleMappingChange(v, e.target.value, 'constant')}
                                  className="tw-w-48 tw-h-[40px] tw-rounded-xl"
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {requiresMedia && (
                    <div className={variables.length > 0 ? "tw-pt-4 tw-border-t tw-border-b-0 tw-border-r-0 tw-border-l-0 tw-border-solid tw-border-slate-100" : ""}>
                      <label className="tw-text-xs tw-font-bold tw-text-slate-700 tw-uppercase tw-tracking-wider tw-block tw-mb-1">
                        Select Template Media <span className="tw-text-rose-500">*</span>
                      </label>
                      <p className="tw-text-xs tw-text-slate-500 tw-m-0 tw-mb-3">
                        This template requires an <strong>{headerComponent?.format}</strong> header file.
                      </p>

                      <div className="tw-flex tw-items-center tw-gap-3">
                        <input
                          type="file"
                          ref={campaignFileInputRef}
                          style={{ display: "none" }}
                          accept={getAcceptString(headerComponent?.format)}
                          onChange={handleDirectMediaSelect}
                        />
                        <Button
                          type="primary"
                          icon={<FileImageOutlined />}
                          onClick={() => campaignFileInputRef.current?.click()}
                          className="!tw-h-10 !tw-rounded-xl !tw-bg-[#5D44FF] hover:!tw-bg-[#4B34E6] !tw-border-none !tw-font-bold tw-inline-flex tw-items-center tw-gap-2 !tw-shadow-sm"
                        >
                          {selectedMediaObj ? "Change Media File" : "Select Media File"}
                        </Button>

                        {selectedMediaObj && (
                          <div className="tw-flex tw-items-center tw-gap-2.5 tw-px-3.5 tw-py-2 tw-rounded-xl tw-bg-emerald-50 tw-text-emerald-800 tw-border tw-border-solid tw-border-emerald-200/80 tw-text-xs tw-font-medium">
                            <span className="tw-w-5 tw-h-5 tw-rounded-full tw-bg-emerald-500 tw-text-white tw-flex tw-items-center tw-justify-center tw-text-[11px] tw-font-bold tw-flex-shrink-0">
                              ✓
                            </span>
                            <span className="tw-truncate tw-max-w-[220px] tw-font-semibold">
                              {selectedMediaObj.fileName || selectedMediaObj.name || "Media File Attached"}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="tw-mt-6 tw-flex tw-justify-center tw-items-center tw-gap-3">
              {onSaveDraft && (
                <Button
                  onClick={onSaveDraft}
                  className="!tw-h-11 !tw-rounded-full !tw-border-slate-200/90 !tw-bg-white hover:!tw-bg-slate-50 !tw-px-6 tw-text-sm tw-font-semibold tw-text-slate-700 tw-shadow-sm tw-flex tw-items-center tw-gap-2"
                >
                  <FileOutlined />
                  Save Draft
                </Button>
              )}
              <Button
                type="primary"
                onClick={onContinue}
                disabled={!isMappingComplete}
                className={`!tw-h-11 !tw-rounded-full !tw-border-0 !tw-px-8 ${isMappingComplete ? '!tw-bg-theme' : '!tw-bg-gray-300'}`}
              >
                Continue
                <RightOutlined />
              </Button>
            </div>
          </>
        )}
      </div>

      <WhatsAppTemplateComposer
        visible={isCreateTemplateOpen}
        onCancel={() => setIsCreateTemplateOpen(false)}
        onSuccess={() => {
          setIsCreateTemplateOpen(false);
          if (fetchTemplates) fetchTemplates();
        }}
      />

      <MediaPickerModal
        visible={isMediaPickerVisible}
        onClose={() => setIsMediaPickerVisible(false)}
        onSelect={onMediaSelect}
        requiredType={headerComponent?.format ? headerComponent.format.toUpperCase() : null}
      />

      <div className="tw-flex tw-flex-col tw-gap-6">
        <WhatsAppPhonePreview
          templateTitle={title}
          body={bodyText}
          mappingConfig={mappingConfig}
          selectedMediaObj={selectedMediaObj}
        />
      </div>
    </div>
  );
};

const ReviewStep = ({
  campaignName,
  selectedAudience,
  scheduleMode,
  selectedDate,
  errors = {},
  templateTitle,
  templateBody,
  mappingConfig,
  selectedMediaObj,
  onScheduleModeChange,
  onDateSelect,
  onLaunch,
  onSaveDraft,
  isEdit = false,
}) => (
  <div className="tw-grid tw-grid-cols-1 tw-gap-6 xl:tw-grid-cols-[1.1fr_0.85fr]">
    <div>
      <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-3">
        <div className="tw-rounded-[18px] tw-border tw-border-solid tw-border-border-primary tw-bg-[linear-gradient(180deg,#FFFFFF_0%,#F9FBFF_100%)] tw-p-5 tw-flex tw-flex-col tw-justify-between">
          <div>
            <div className="tw-text-[10px] tw-font-semibold tw-uppercase tw-text-sub-text">
              Campaign Name
            </div>
            <div className="tw-mt-2 tw-text-xl tw-font-bold tw-leading-tight tw-text-black tw-truncate" title={campaignName}>
              {campaignName || "Untitled Campaign"}
            </div>
          </div>
          {selectedAudience?.name && (
            <div className="tw-mt-2 tw-text-xs tw-text-sub-text tw-truncate" title={selectedAudience.name}>
              List: {selectedAudience.name}
            </div>
          )}
        </div>

        <div className="tw-rounded-[18px] tw-border tw-border-solid tw-border-border-primary tw-bg-white tw-p-5 tw-flex tw-flex-col tw-justify-between">
          <div>
            <div className="tw-text-[10px] tw-font-semibold tw-uppercase tw-text-sub-text">
              Recipients
            </div>
            <div className="tw-mt-2 tw-flex tw-items-baseline tw-gap-1.5">
              <span className="tw-text-[30px] tw-font-bold tw-text-black tw-leading-none">
                {selectedAudience?.contacts || 0}
              </span>
              <span className="tw-text-xs tw-font-medium tw-text-sub-text">
                Contacts
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="tw-mt-3 tw-rounded-[18px] tw-border tw-border-solid tw-border-border-primary tw-bg-[linear-gradient(180deg,#FFFFFF_0%,#F0FFF4_100%)] tw-p-5">
        <div className="tw-flex tw-flex-col tw-gap-4 md:tw-flex-row md:tw-items-start md:tw-justify-between">
          <div>
            <div className="tw-text-sm tw-font-semibold tw-text-black">
              When should this go out?
            </div>
            <div className="tw-mt-1 tw-text-xs tw-text-sub-text">
              Send immediately or schedule Campaign
            </div>
          </div>
          <div className="tw-inline-flex tw-items-center tw-rounded-full tw-bg-[#F3F4F6] tw-p-1 tw-border tw-border-solid tw-border-[#E5E7EB]">
            <button
              type="button"
              onClick={() => onScheduleModeChange("send")}
              className={`tw-flex tw-items-center tw-rounded-full tw-px-5 tw-py-2 tw-text-[12px] tw-font-bold tw-border-none tw-cursor-pointer tw-outline-none tw-transition-all ${scheduleMode === "send"
                  ? "tw-bg-[#5D44FF] tw-text-white tw-shadow-sm"
                  : "tw-bg-transparent tw-text-[#575C66] hover:tw-text-[#2E333F]"
                }`}
            >
              <SendOutlined className="tw-text-xs" />
              <span className="tw-ml-2">Send Now</span>
            </button>
            <button
              type="button"
              onClick={() => onScheduleModeChange("schedule")}
              className={`tw-flex tw-items-center tw-rounded-full tw-px-5 tw-py-2 tw-text-[12px] tw-font-bold tw-border-none tw-cursor-pointer tw-outline-none tw-transition-all ${scheduleMode === "schedule"
                  ? "tw-bg-[#5D44FF] tw-text-white tw-shadow-sm"
                  : "tw-bg-transparent tw-text-[#575C66] hover:tw-text-[#2E333F]"
                }`}
            >
              <CalendarOutlined className="tw-text-xs" />
              <span className="tw-ml-2">Schedule</span>
            </button>
          </div>
        </div>

        {scheduleMode === "send" ? (
          <div className="tw-mt-4 tw-rounded-full tw-bg-[linear-gradient(90deg,#A7F3D0_0%,#D9F99D_100%)] tw-px-5 tw-py-4">
            <div className="tw-flex tw-items-center tw-gap-3">
              <div className="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-full tw-bg-[#25D366] tw-text-white">
                <SendOutlined className="tw-text-[12px]" />
              </div>
              <div>
                <div className="tw-text-sm tw-font-semibold tw-text-text-primary">
                  Send immediately
                </div>
                <div className="tw-text-xs tw-text-sub-text">
                  Your message will be delivered to {selectedAudience?.contacts || 0} contacts within a few
                  minutes.
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="tw-mt-4 tw-rounded-[14px] tw-border tw-border-solid tw-border-border-primary tw-bg-white tw-p-5">
            <div className="tw-mb-3 tw-text-[13px] tw-font-semibold tw-text-text-primary tw-flex tw-items-center tw-gap-1">
              Select Launch Date & Time <span className="tw-text-rose-500">*</span>
            </div>
            <DatePicker
              showTime
              format="YYYY-MM-DD HH:mm"
              value={selectedDate}
              onChange={onDateSelect}
              status={errors?.scheduledDate ? "error" : ""}
              className="tw-w-full !tw-h-[44px] !tw-rounded-[10px]"
              disabledDate={(current) => current && current.valueOf() < Date.now()}
            />
            {errors?.scheduledDate && (
              <div className="tw-mt-1.5 tw-text-xs tw-text-rose-500 tw-font-medium">
                {errors.scheduledDate}
              </div>
            )}
            <div className="tw-mt-3 tw-text-[11px] tw-text-sub-text">
              The campaign will be executed automatically at the scheduled time. Ensure the timezone matches your target audience.
            </div>
          </div>
        )}
      </div>

      <div className="tw-mt-4 tw-flex tw-justify-center tw-items-center tw-gap-3">
        {onSaveDraft && (
          <Button
            onClick={onSaveDraft}
            className="!tw-h-11 !tw-rounded-full !tw-border-slate-200/90 !tw-bg-white hover:!tw-bg-slate-50 !tw-px-6 tw-text-sm tw-font-semibold tw-text-slate-700 tw-shadow-sm tw-flex tw-items-center tw-gap-2"
          >
            <FileOutlined />
            Save Draft
          </Button>
        )}
        <Button
          type="primary"
          onClick={onLaunch}
          className="!tw-h-11 !tw-rounded-full !tw-border-0 !tw-bg-theme !tw-px-8"
        >
          <SendOutlined />
          {isEdit ? "Update Campaign" : "Launch Campaign"}
        </Button>
      </div>
    </div>

    <WhatsAppPhonePreview
      templateTitle={templateTitle}
      body={templateBody}
      mappingConfig={mappingConfig}
      selectedMediaObj={selectedMediaObj}
    />
  </div>
);

const WhatsAppCreateCampaign = ({ onBack, onCancel }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const duplicateCampaign = location.state?.duplicateCampaign;
  const stateEditCampaign = location.state?.editCampaign;

  const [fetchedCampaign, setFetchedCampaign] = useState(null);
  const [isLoadingCampaign, setIsLoadingCampaign] = useState(false);

  const editCampaign = stateEditCampaign || fetchedCampaign;
  const initCampaign = duplicateCampaign || editCampaign;

  const [currentStep, setCurrentStep] = useState(1);
  const [errors, setErrors] = useState({});
  const [campaignName, setCampaignName] = useState(
    initCampaign ? (duplicateCampaign ? `${duplicateCampaign.name} (Copy)` : editCampaign.name) : ""
  );

  const [assignmentStrategy, setAssignmentStrategy] = useState(initCampaign?.assignmentStrategy || "SELF_ASSIGNMENT");
  const [assignmentWeights, setAssignmentWeights] = useState(initCampaign?.assignmentWeights || []);
  const [isPublic, setIsPublic] = useState(initCampaign ? initCampaign.isPublic : true);

  const [wabaInfo, setWabaInfo] = useState(null);
  const [audiences, setAudiences] = useState([]);
  const [templates, setTemplates] = useState([]);

  const [whatsappPhoneNumberId, setWhatsappPhoneNumberId] = useState(initCampaign ? initCampaign.whatsappPhoneNumberId : null);
  const [selectedListIds, setSelectedListIds] = useState(initCampaign ? (initCampaign.campaignAudienceIds || []) : []);
  const [selectedTemplateId, setSelectedTemplateId] = useState(initCampaign ? initCampaign.whatsappMessageTemplateId : null);
  const [selectedMediaObj, setSelectedMediaObj] = useState(initCampaign && initCampaign.mediaAssetId ? { id: initCampaign.mediaAssetId, url: initCampaign.mediaAssetUrl } : null);

  const [mappingConfig, setMappingConfig] = useState(
    initCampaign && initCampaign.mappingConfig
      ? (typeof initCampaign.mappingConfig === 'string' ? JSON.parse(initCampaign.mappingConfig) : initCampaign.mappingConfig)
      : { header: {}, body: {}, buttons: {} }
  );
  const [scheduleMode, setScheduleMode] = useState(initCampaign && initCampaign.scheduledDate ? "schedule" : "schedule");
  const [selectedDate, setSelectedDate] = useState(
    initCampaign && initCampaign.scheduledDate ? dayjs(initCampaign.scheduledDate) : null
  );

  const [audienceTag, setAudienceTag] = useState("All");
  const [audienceQuery, setAudienceQuery] = useState("");

  useEffect(() => {
    if (id && !stateEditCampaign && !fetchedCampaign) {
      setIsLoadingCampaign(true);
      getAuthMethod(`/api/whatsapp-campaigns/${id}`)
        .then(res => {
          if (res.data) {
            setFetchedCampaign(res.data);
          }
        })
        .catch(err => {
          console.error("Error fetching campaign by ID:", err);
          message.error("Failed to load campaign details.");
        })
        .finally(() => {
          setIsLoadingCampaign(false);
        });
    }
  }, [id, stateEditCampaign, fetchedCampaign]);

  useEffect(() => {
    if (initCampaign) {
      if (duplicateCampaign) {
        setCampaignName(`${duplicateCampaign.name} (Copy)`);
      } else if (editCampaign) {
        setCampaignName(editCampaign.name || "");
      }
      if (initCampaign.isPublic !== undefined) {
        setIsPublic(initCampaign.isPublic);
      }
      if (initCampaign.whatsappPhoneNumberId) {
        setWhatsappPhoneNumberId(initCampaign.whatsappPhoneNumberId);
      }
      if (initCampaign.campaignAudienceIds) {
        setSelectedListIds(initCampaign.campaignAudienceIds);
      }
      if (initCampaign.whatsappMessageTemplateId) {
        setSelectedTemplateId(initCampaign.whatsappMessageTemplateId);
      }
      if (initCampaign.mappingConfig) {
        try {
          const parsed = typeof initCampaign.mappingConfig === 'string'
            ? JSON.parse(initCampaign.mappingConfig)
            : initCampaign.mappingConfig;
          setMappingConfig(parsed);
        } catch (e) {
          console.error("Error parsing mappingConfig:", e);
        }
      }
      if (initCampaign.scheduledDate) {
        setScheduleMode("schedule");
        setSelectedDate(dayjs(initCampaign.scheduledDate));
      }
    }
  }, [initCampaign]);

  const [dynamicTags, setDynamicTags] = useState(["All"]);

  const sortLatestTemplates = (list) => {
    if (!Array.isArray(list)) return [];
    return [...list].sort((a, b) => {
      const dateA = new Date(
        a.createdDate || a.createdAt || a.updatedAt || a.lastModifiedDate || 0
      ).getTime();
      const dateB = new Date(
        b.createdDate || b.createdAt || b.updatedAt || b.lastModifiedDate || 0
      ).getTime();
      if (dateA && dateB && dateA !== dateB) return dateB - dateA;
      const idA = typeof a.id === "number" ? a.id : parseInt(a.id, 10);
      const idB = typeof b.id === "number" ? b.id : parseInt(b.id, 10);
      if (!isNaN(idA) && !isNaN(idB)) return idB - idA;
      return 0;
    });
  };

  useEffect(() => {
    getAuthMethod("/api/whatsapp/accounts/status").then(res => setWabaInfo(res.data)).catch(console.error);
    getAuthMethod("/api/whatsapp/templates?size=100").then(res => setTemplates(sortLatestTemplates(res.data || []))).catch(console.error);
  }, []);

  useEffect(() => {
    const fetchDynamicTags = async () => {
      const tagsSet = new Set();

      // 1. Fetch tags from API endpoint
      try {
        const tagsRes = await getAuthMethod("api/tags?type.equals=AUDIENCE");
        if (tagsRes && tagsRes.data) {
          const tagItems = Array.isArray(tagsRes.data) ? tagsRes.data : (tagsRes.data.tagsDTOs || []);
          tagItems.forEach(t => {
            const name = typeof t === 'string' ? t : (t.name || t.label || t.value);
            if (name && typeof name === 'string' && name.trim()) {
              tagsSet.add(name.trim());
            }
          });
        }
      } catch (e) {
        console.error("Error fetching tags from API:", e);
      }

      // 2. Fetch all audiences to collect unique tags
      try {
        const audRes = await getAuthMethod("/api/campaign-audiences?size=200");
        if (audRes && audRes.data) {
          const list = Array.isArray(audRes.data) ? audRes.data : (audRes.data.content || []);
          list.forEach(a => {
            if (Array.isArray(a.tags)) {
              a.tags.forEach(t => {
                const name = typeof t === 'string' ? t : (t.name || t.label || t.value);
                if (name && typeof name === 'string' && name.trim()) {
                  tagsSet.add(name.trim());
                }
              });
            } else if (typeof a.tags === 'string' && a.tags.trim()) {
              a.tags.split(',').forEach(t => t.trim() && tagsSet.add(t.trim()));
            }
          });
        }
      } catch (e) {
        console.error("Error fetching audiences for tags:", e);
      }

      const tagArray = Array.from(tagsSet).filter(Boolean);
      if (tagArray.length > 0) {
        setDynamicTags(["All", ...tagArray]);
      }
    };

    fetchDynamicTags();
  }, []);

  useEffect(() => {
    let url = "/api/campaign-audiences?size=100";
    if (audienceTag !== "All") {
      url += `&tag.equals=${encodeURIComponent(audienceTag)}`;
    }
    if (audienceQuery) {
      url += `&keyword.contains=${encodeURIComponent(audienceQuery)}`;
    }
    getAuthMethod(url).then(res => setAudiences(res.data || [])).catch(console.error);
  }, [audienceTag, audienceQuery]);

  const formatAudienceDate = (a) => {
    const calcDate = a.lastCalculatedAt || a.lastModifiedDate || a.updatedAt || a.updatedDate;
    if (calcDate) {
      const d = new Date(calcDate);
      if (!isNaN(d.getTime())) {
        return "Updated " + d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
      }
    }

    const createDate = a.createdDate || a.createdAt || a.createdOn || a.date;
    if (createDate) {
      const d = new Date(createDate);
      if (!isNaN(d.getTime())) {
        return "Created " + d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
      }
    }

    return "Recently added";
  };

  const getAudienceTimestamp = (a) => {
    const dateVal = a.lastCalculatedAt || a.lastModifiedDate || a.updatedAt || a.updatedDate || a.createdDate || a.createdAt || a.createdOn || a.date;
    if (dateVal) {
      const t = new Date(dateVal).getTime();
      if (!isNaN(t)) return t;
    }
    return typeof a.id === "number" ? a.id : 0;
  };

  const contactLists = audiences
    .map(a => ({
      id: a.id,
      name: a.name,
      contacts: a.whatsappReadyCount || 0,
      lastUsed: formatAudienceDate(a),
      metricLabel: "WhatsApp Ready Contacts",
      tags: a.tags || [],
      engagement: 0,
      rawDate: getAudienceTimestamp(a),
    }))
    .sort((a, b) => (b.rawDate || 0) - (a.rawDate || 0));

  const selectedAudiences = contactLists.filter((item) => selectedListIds.includes(item.id));
  const totalSelectedContacts = selectedAudiences.reduce((acc, curr) => acc + (curr.contacts || 0), 0);
  const selectedTemplate =
    templates.find((item) => item.id === selectedTemplateId) || templates[0] || {};

  const getVariablesFromTemplate = (t) => {
    let vars = [];
    if (!t) return vars;
    let comps = t.components;
    if (typeof comps === 'string') {
      try { comps = JSON.parse(comps); } catch (e) { comps = []; }
    }
    if (Array.isArray(comps)) {
      const bodyComp = comps.find(c => c.type === 'BODY');
      if (bodyComp && bodyComp.text) {
        const matches = bodyComp.text.match(/\{\{(\d+)\}\}/g);
        if (matches) {
          vars = [...new Set(matches.map(m => m.replace(/[{}]/g, '')))];
        }
      }
    }
    return vars;
  };

  const isMappingComplete = useMemo(() => {
    if (!selectedTemplateId) return false;
    const vars = getVariablesFromTemplate(selectedTemplate);
    if (vars.length === 0) return true;
    return vars.every(v => {
      const map = mappingConfig?.body?.[v];
      if (!map) return false;
      if (map.type === 'audience_field' && map.attribute) return true;
      if (map.type === 'constant' && map.value) return true;
      return false;
    });
  }, [selectedTemplate, selectedTemplateId, mappingConfig]);

  const validateStep1 = () => {
    const errs = {};
    if (!campaignName || !campaignName.trim()) {
      errs.campaignName = "Campaign name is required.";
    }
    if (!whatsappPhoneNumberId) {
      errs.whatsappPhoneNumberId = "Sender phone number is required.";
    }
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      const firstErr = Object.values(errs)[0];
      message.error(firstErr);
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!selectedListIds || selectedListIds.length === 0) {
      setErrors({ audience: "Please select at least one contact list for the audience." });
      message.error("Please select at least one contact list for the audience.");
      return false;
    }
    setErrors({});
    return true;
  };

  const validateStep3 = () => {
    if (!selectedTemplateId) {
      setErrors({ template: "Please select a message template." });
      message.error("Please select a message template.");
      return false;
    }
    if (!isMappingComplete) {
      setErrors({ templateMapping: "Please complete mapping for all template variables." });
      message.error("Please complete variable mapping for all template variables.");
      return false;
    }
    setErrors({});
    return true;
  };

  const validateStep4 = () => {
    if (scheduleMode === "schedule" && !selectedDate) {
      setErrors({ scheduledDate: "Please select a launch date and time for the scheduled campaign." });
      message.error("Please select a launch date and time for the scheduled campaign.");
      return false;
    }
    setErrors({});
    return true;
  };

  const goToStep = (targetStep) => {
    if (targetStep <= currentStep) {
      setCurrentStep(targetStep);
      return;
    }
    if (currentStep === 1 && !validateStep1()) return;
    if (currentStep === 2 && !validateStep2()) return;
    if (currentStep === 3 && !validateStep3()) return;
    setCurrentStep(targetStep);
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    } else {
      if (onBack) {
        onBack();
      } else if (onCancel) {
        onCancel();
      } else {
        navigate("/whatsapp-campaigns");
      }
    }
  };

  const handleLaunch = () => {
    if (!validateStep1()) {
      setCurrentStep(1);
      return;
    }
    if (!validateStep2()) {
      setCurrentStep(2);
      return;
    }
    if (!validateStep3()) {
      setCurrentStep(3);
      return;
    }
    if (!validateStep4()) {
      setCurrentStep(4);
      return;
    }

    const finalMappingConfig = { ...mappingConfig };
    if (selectedMediaObj) {
      finalMappingConfig.header = {
        isDynamic: false,
        value: selectedMediaObj.id.toString()
      };
    }

    const payload = {
      ...(editCampaign ? { id: editCampaign.id } : {}),
      name: campaignName.trim(),
      isPublic,
      assignmentStrategy,
      assignmentWeights: assignmentWeights.map(w => ({
        userId: w.userId,
        assignmentWeight: w.assignmentWeight
      })),
      whatsappPhoneNumberId,
      campaignAudienceIds: selectedListIds,
      whatsappMessageTemplateId: selectedTemplateId,
      mediaAssetId: selectedMediaObj ? selectedMediaObj.id : null,
      mappingConfig: JSON.stringify(finalMappingConfig),
      scheduledDate: scheduleMode === "schedule" && selectedDate ? selectedDate.toISOString() : null,
      status: "SCHEDULED",
    };

    const requestPromise = editCampaign
      ? putAuthMethod(`/api/whatsapp-campaigns`, editCampaign.id, payload)
      : postAuthMethod("/api/whatsapp-campaigns", payload);

    requestPromise
      .then(res => {
        message.success(`Campaign ${editCampaign ? 'updated' : 'launched'} successfully!`);
        navigate("/whatsapp-campaigns");
      })
      .catch(err => {
        console.error(err);
        message.error(`Failed to ${editCampaign ? 'update' : 'launch'} campaign: ` + (err.response?.data?.title || err.response?.data?.message || err.message));
      });
  };

  const handleSaveDraft = () => {
    if (!validateStep1()) {
      setCurrentStep(1);
      return;
    }

    const finalMappingConfig = { ...mappingConfig };
    if (selectedMediaObj) {
      finalMappingConfig.header = {
        isDynamic: false,
        value: selectedMediaObj.id.toString()
      };
    }

    const payload = {
      ...(editCampaign ? { id: editCampaign.id } : {}),
      name: campaignName.trim(),
      isPublic,
      assignmentStrategy,
      assignmentWeights: assignmentWeights.map(w => ({
        userId: w.userId,
        assignmentWeight: w.assignmentWeight
      })),
      whatsappPhoneNumberId,
      campaignAudienceIds: selectedListIds,
      whatsappMessageTemplateId: selectedTemplateId,
      mediaAssetId: selectedMediaObj ? selectedMediaObj.id : null,
      mappingConfig: JSON.stringify(finalMappingConfig),
      scheduledDate: scheduleMode === "schedule" && selectedDate ? selectedDate.toISOString() : null,
      status: "DRAFT"
    };

    const requestPromise = editCampaign
      ? putAuthMethod(`/api/whatsapp-campaigns`, editCampaign.id, payload)
      : postAuthMethod("/api/whatsapp-campaigns", payload);

    requestPromise
      .then(res => {
        message.success(`Campaign saved as draft successfully!`);
        navigate("/whatsapp-campaigns");
      })
      .catch(err => {
        console.error(err);
        message.error(`Failed to save draft: ` + (err.response?.data?.title || err.response?.data?.message || err.message));
      });
  };

  const renderCurrentStep = () => {
    if (currentStep === 1) {
      return (
        <DetailsStep
          wabaInfo={wabaInfo}
          whatsappPhoneNumberId={whatsappPhoneNumberId}
          campaignName={campaignName}
          isPublic={isPublic}
          errors={errors}
          onIsPublicChange={setIsPublic}
          onPhoneNumberChange={(val) => {
            setWhatsappPhoneNumberId(val);
            if (errors.whatsappPhoneNumberId) {
              setErrors(prev => ({ ...prev, whatsappPhoneNumberId: null }));
            }
          }}
          onCampaignNameChange={(val) => {
            setCampaignName(val);
            if (errors.campaignName) {
              setErrors(prev => ({ ...prev, campaignName: null }));
            }
          }}
          assignmentStrategy={assignmentStrategy}
          onAssignmentStrategyChange={setAssignmentStrategy}
          assignmentWeights={assignmentWeights}
          onAssignmentWeightsChange={setAssignmentWeights}
        />
      );
    }

    if (currentStep === 2) {
      return (
        <AudienceStep
          contactLists={contactLists}
          selectedListIds={selectedListIds}
          errors={errors}
          onSelectList={(ids) => {
            setSelectedListIds(ids);
            if (errors.audience) setErrors(prev => ({ ...prev, audience: null }));
          }}
          onContinue={() => {
            if (validateStep2()) {
              setCurrentStep(3);
            }
          }}
          onSaveDraft={handleSaveDraft}
          selectedTag={audienceTag}
          searchQuery={audienceQuery}
          onTagSelect={setAudienceTag}
          onSearchChange={setAudienceQuery}
          phoneNumbers={wabaInfo?.phoneNumbers || []}
          audienceTags={dynamicTags}
        />
      );
    }

    if (currentStep === 3) {
      return (
        <MessageStep
          templates={templates}
          selectedTemplateId={selectedTemplateId}
          errors={errors}
          onSelectTemplate={(id) => {
            setSelectedTemplateId(id);
            
            const selectedTpl = templates.find(t => t.id === id);
            if (selectedTpl && selectedTpl.defaultMediaAssetId) {
              getAuthMethod(`/api/media/${selectedTpl.defaultMediaAssetId}`)
                .then(res => {
                  setSelectedMediaObj(res.data);
                })
                .catch(err => {
                  console.error("Failed to load default media asset", err);
                  setSelectedMediaObj(null);
                });
            } else {
              setSelectedMediaObj(null);
            }
            if (errors.template) setErrors(prev => ({ ...prev, template: null }));
          }}
          mappingConfig={mappingConfig}
          onMappingChange={(cfg) => {
            setMappingConfig(cfg);
            if (errors.templateMapping) setErrors(prev => ({ ...prev, templateMapping: null }));
          }}
          onContinue={() => {
            if (validateStep3()) {
              setCurrentStep(4);
            }
          }}
          onSaveDraft={handleSaveDraft}
          selectedMediaObj={selectedMediaObj}
          onMediaSelect={setSelectedMediaObj}
          fetchTemplates={() => getAuthMethod("/api/whatsapp/templates?size=100").then(res => setTemplates(res.data || [])).catch(console.error)}
        />
      );
    }

    // Try to parse components to get body text for preview
    const getBodyPreview = (t) => {
      let text = t.body || "";
      if (t.components) {
        let comps = t.components;
        if (typeof comps === 'string') {
          try { comps = JSON.parse(comps); } catch (e) { comps = []; }
        }
        if (Array.isArray(comps)) {
          const bodyComp = comps.find(c => c.type === 'BODY');
          if (bodyComp) text = bodyComp.text;
        }
      }
      return text;
    };
    const title = selectedTemplate?.name || selectedTemplate?.title || "Template";
    const bodyText = selectedTemplate ? getBodyPreview(selectedTemplate) : "";

    return (
      <ReviewStep
        campaignName={campaignName}
        selectedAudience={selectedAudiences.length > 0 ? { name: selectedAudiences.map(a => a.name).join(', '), contacts: totalSelectedContacts } : { name: "None", contacts: 0 }}
        scheduleMode={scheduleMode}
        selectedDate={selectedDate}
        errors={errors}
        templateTitle={title}
        templateBody={bodyText}
        mappingConfig={mappingConfig}
        selectedMediaObj={selectedMediaObj}
        onScheduleModeChange={setScheduleMode}
        onDateSelect={(val) => {
          setSelectedDate(val);
          if (errors.scheduledDate) setErrors(prev => ({ ...prev, scheduledDate: null }));
        }}
        onLaunch={handleLaunch}
        onSaveDraft={handleSaveDraft}
        isEdit={!!editCampaign}
      />
    );
  };

  const audienceCount = selectedAudiences.reduce((acc, curr) => acc + curr.contacts, 0);

  if (isLoadingCampaign) {
    return (
      <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-full tw-min-h-[500px]">
        <Spin size="large" />
        <p className="tw-mt-4 tw-text-base tw-text-slate-600">Loading campaign details...</p>
      </div>
    );
  }

  return (
    <div className="tw-h-full tw-overflow-y-auto tw-bg-white tw-px-4 tw-py-5 md:tw-px-8 md:tw-py-8">
      <div className="tw-mx-auto tw-flex tw-flex-col xl:tw-flex-row tw-gap-8 tw-max-w-[1400px]">
        {/* Left Column (Main Wizard) */}
        <div className="tw-flex-1 tw-min-w-0">
          <div className="tw-flex tw-items-center tw-justify-between tw-gap-4">
            <div className="tw-flex tw-items-center tw-gap-4">
              <div
                className="tw-flex tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-xl tw-p-2.5 tw-bg-slate-100 hover:tw-bg-slate-200/60 tw-text-slate-600 tw-transition-colors"
                onClick={handleBack}
                role="button"
                aria-label="Back"
              >
                <ArrowLeftOutlined className="tw-text-base" />
              </div>
              <div className="tw-flex tw-flex-col tw-justify-center tw-gap-0.5">
                <h1 className="tw-text-xl tw-font-bold tw-mb-0 tw-text-slate-800 tw-font-inter">
                  {editCampaign ? "Edit Campaign" : "Create Campaign"}
                </h1>
                <p className="tw-text-xs tw-mb-0 tw-text-slate-500 tw-font-medium">
                  Step {currentStep} of 4
                </p>
              </div>
            </div>

            <div className="tw-flex tw-gap-2">
              <Button
                onClick={() => navigate("/whatsapp-campaigns")}
                className="!tw-h-[38px] !tw-bg-white hover:!tw-bg-slate-50 !tw-border-slate-200/80 !tw-rounded-xl !tw-px-4 tw-text-xs tw-font-semibold tw-text-slate-700 tw-shadow-sm tw-flex tw-items-center tw-gap-2"
              >
                <UnorderedListOutlined />
                Campaign List
              </Button>
            </div>
          </div>

          <div className="tw-mt-10">
            <CampaignStepper currentStep={currentStep} onStepClick={goToStep} />
          </div>
          <div className="tw-mt-10">{renderCurrentStep()}</div>

          {currentStep === 1 ? (
            <div className="tw-mt-8 tw-flex tw-justify-center tw-items-center tw-gap-3">
              <Button
                onClick={handleSaveDraft}
                className="!tw-h-11 !tw-rounded-full !tw-border-slate-200/90 !tw-bg-white hover:!tw-bg-slate-50 !tw-px-6 tw-text-sm tw-font-semibold tw-text-slate-700 tw-shadow-sm tw-flex tw-items-center tw-gap-2"
              >
                <FileOutlined />
                Save Draft
              </Button>
              <Button
                type="primary"
                onClick={() => {
                  if (validateStep1()) {
                    setCurrentStep(2);
                  }
                }}
                className="!tw-h-11 !tw-rounded-full !tw-border-0 !tw-bg-theme !tw-px-8 !tw-shadow-[0_12px_24px_rgba(93,68,255,0.24)]"
              >
                Continue
                <RightOutlined />
              </Button>
            </div>
          ) : null}
        </div>

        {/* Right Column (Sticky Health Panel) */}
        <div className="tw-w-full xl:tw-w-[320px] tw-shrink-0">
          <WhatsAppCampaignHealthPanel
            phoneNumbers={wabaInfo?.phoneNumbers || []}
            selectedPhoneId={whatsappPhoneNumberId}
            audienceCount={audienceCount}
            isTemplateSelected={!!selectedTemplateId}
            isAudienceSelected={selectedListIds.length > 0}
          />
        </div>
      </div>
    </div>
  );
};

export default WhatsAppCreateCampaign;
